import { LocationStrategy } from '@angular/common';
import { EventEmitter, Injectable } from '@angular/core';
import {
  OidcSecurityService,
  AbstractSecurityStorage,
  AuthenticatedResult,
  PublicEventsService,
  EventTypes,
  OpenIdConfiguration
} from 'angular-auth-oidc-client';
import {
  forkJoin,
  Observable,
  filter,
  race,
  map,
  timer,
  catchError,
  throwError,
  takeUntil,
  distinctUntilKeyChanged,
  Subject,
  lastValueFrom
} from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { AppLoadService } from '../app-loader.service';
import {
  SecurityConfig,
  SecurityConfigInjectionToken
} from '../configuration/security.config';
import { RedirectService } from '../services/redirect.service';
import { StorageService } from '../services/storage.service';
import { Jwt } from '../web-tokens/jwt';
import { OidcAuthenticationService } from './services/oidc-authentication.service';
import { OidcConfigService } from './services/oidc-config.service';

/**
 * The Oidc Loader will ensure that any and all configuration
 * is setup prior to the application starting, without this
 * a user will not be present during startup if already authenticated
 */
@Injectable()
export class OidcLoaderService extends AppLoadService {
  private static AUTHNONCE: string = 'authNonce';
  private _oidSecurityService: OidcSecurityService | null = null;
  private _configService: OidcConfigService | null = null;
  private _locationStrategy: LocationStrategy | null = null;
  private _secConfig: SecurityConfig | null = null;
  private _storageService: StorageService | null = null;
  private _redirectService: RedirectService | null = null;
  private _oidcSecurityStorage: AbstractSecurityStorage | null = null;
  private redirectPath: string | null = null;
  private hashedEntryPoint: string = '';
  private isAuthenticated$: Observable<AuthenticatedResult> =
    this.oidcSecurityService.isAuthenticated$;
  private _eventService: PublicEventsService | null = null;
  private _oidcAuthenticationService: OidcAuthenticationService | null = null;
  private isNewAuthentication = false;
  private onDestroy$ = new Subject<boolean>();
  private onDestroyUserData$ = new Subject<boolean>();
  /**
   * The releaseEvent acts as a gate control for the post initialize. It is used
   * to prevent the application from loading if an auto-login request is pending
   */
  private releaseEvent: EventEmitter<boolean> = new EventEmitter<boolean>();

  public override async preInitialize(): Promise<any> {
    super.preInitialize();

    // We can run into a race condition where the silent-renew
    // forces us to the unauthorized page before the auto-login
    // process starts, this ensures we have the landing url correct
    this.hashedEntryPoint = this.locationStrategy.path(true);
    this.redirectPath = this.redirectService.currentPath;

    this.eventService
      .registerForEvents()
      .pipe(filter(n => n?.type === EventTypes.NewAuthenticationResult))
      .subscribe((value: any) => {
        this.isNewAuthentication = true;
      });

    // We need to listen to each authorization result in order to correct EC-4636 (fragile fix)
    // OIDC Version 15 upgrade: onAuthorizationResult is removed
    // https://jira.csx.com/browse/SECDEV-563 is fixed in oidc v-15. hence, removing this.resetUser()

    return await lastValueFrom(
      this.oidcSecurityService
        // OIDC Version 15 upgrade: getIsAuthorized is removed
        .isAuthenticated()
        .pipe(
          catchError(error => {
            console.error('Error in isAuthenticated() ', error);
            return throwError(() => error);
          })
        )
        .pipe(
          take(1),
          tap((isAuthenticated: boolean) => {
            this.resetUserData(isAuthenticated);
          })
        )
        .pipe(tap(() => console.debug('preInitialize complete')))
    );
  }

  public override async initialize(): Promise<any> {
    super.initialize();

    // OIDC Version 15 upgrade: removing this.listenForSlientRenewFlag(); as it is handled by library

    // Initialization should only be considered complete once
    // we have setup the module and pre-initialization has completed
    // OIDC Version 15 upgrade: notification for load of configuration is now available via event service, so we need not listen to event loadConfiguration
    return await lastValueFrom(
      this.eventService
        .registerForEvents()
        .pipe(
          catchError(error => {
            console.error('Error in ConfigLoaded ', error);
            return throwError(() => error);
          })
        )
        .pipe(
          filter(
            notification => notification?.['type'] === EventTypes.ConfigLoaded
          ),
          take(1)
          // OIDC Version 15 upgrade: removing this.clearSilentRenewFlag() as it is handled by library
        )
        // OIDC Version 15 upgrade: configure client is no more needed, as configuration is loaded as part of module now
        // OIDC Version 15 upgrade: onModuleSetup is called implicitly on config load in version15

        .pipe(tap(() => console.debug('initialize complete')))
    );
  }

  public override async postInitialize(): Promise<any> {
    super.postInitialize();

    // We setup a race between the authorization result and the isAuthorized
    // If neither wins we timeout after 2s
    const race$ = race(
      // OIDC Version 15 upgrade: onAuthorizationResult and isAuthorized has been removed
      this.oidcSecurityService.isAuthenticated().pipe(
        map(isAuthenticated => {
          return isAuthenticated;
        }),
        catchError(error => {
          console.error('Error in checkAuth ', error);
          return throwError(() => error);
        })
      ),
      timer(2000).pipe(
        // backup, if nothing happens after 2 seconds stop waiting and emit
        tap(() => {
          console.debug('PostInitialize: Timeout');
        }),
        map(() => false)
      )
    );

    return await lastValueFrom(
      forkJoin([
        // We do not complete postInitialize until the release gate triggers
        // Wait for both the race and the module setup to complete
        // OIDC Version 15 upgrade: onmodule setup is explicitly completely when configuration is loaded
        race$
          .pipe(
            catchError(error => {
              console.error('Error in Race condition ', error);
              return throwError(() => error);
            })
          )
          .pipe(take(1))
          .pipe(
            tap(res => {
              this.oidcSecurityService
                .checkAuth()
                .pipe(takeUntil(this.onDestroy$))
                .pipe(distinctUntilKeyChanged('isAuthenticated'))
                .subscribe({
                  next: result => {
                    this.onAuthorizationResult(result?.['isAuthenticated']);
                  },
                  error: error => {
                    console.error(
                      'Error processing authentication results ',
                      error
                    );
                  }
                });
            })
          ),
        this.releaseEvent.asObservable().pipe(take(1))
      ])
        .pipe(
          catchError(error => {
            console.error('Error in forkJoin ', error);
            return throwError(() => error);
          })
        )
        .pipe(
          tap(result => {
            console.debug(
              'postInitialize complete; Is Authorized: ',
              result[0]
            );
          })
        )
    );
  }

  /**
   * This has been added to partially fix EC-4636; specifically the base library bug 433
   * If this bug is corrected then this method should be removed
   *
   * It should be noted that this is a fragile fix; this method uses internal knowledge of the
   * library in order to correct the bug as there is no guaranteed way to correct it externally;
   * due to this any future base library updates need to be carefully verified
   * @see https://github.com/damienbod/angular-auth-oidc-client/issues/433
   * @see https://jira.csx.com/browse/EC-4636
   * @since "angular-auth-oidc-client": "9.0.3"
   * @since v1.0.1
   */
  private resetUserData(isAuthenticated: boolean): void {
    let hasUserData = false;

    /*
    The base library stores the ID_Token into an internal variable called _userdata
    This variable is checked during the renew process and if it is not present it will not
    run the renew logic. This variable can be set to '' if the renew process is the first
    thing that authenticates a user (by the user being logged in to another application).
    In order to fix the symptom we need to call the same setter that the internal logic calls
    when it is authenticating and the user data is empty. An alternate approach would be to add
    a user_info endpoint to apigee and enable that functionality; this would incur additional costs
    and should only be done as a last resort.
    */
    if (isAuthenticated) {
      // We use object indexing to get at the internal, private data (which is why this is fragile)
      // OIDC Version 15 upgrade: security service has an observable that contains user data information, we will use
      //  information from this observable to write to store
      this.oidcSecurityService
        .getPayloadFromIdToken()
        .pipe(takeUntil(this.onDestroyUserData$))
        .subscribe({
          next: result => {
            if (!hasUserData && result) {
              hasUserData = true;
              const methodName = 'setUserDataToStore';
              const serviceName = 'userService';
              const setUserData: (
                item: any,
                curr: OpenIdConfiguration,
                all: OpenIdConfiguration[]
              ) => void = this.oidcSecurityService[serviceName][
                methodName
              ].bind(this.oidcSecurityService[serviceName]);

              const currentConfiguration =
                this.oidcSecurityService.getConfigurations()?.[0];
              const allConfiguration =
                this.oidcSecurityService.getConfigurations();
              // OIDC Version 15 upgrade: write user data to current configuration
              if (currentConfiguration?.configId && allConfiguration?.length) {
                setUserData(result, currentConfiguration, allConfiguration);
              }
            }
          },
          error: error => {
            console.error('Error in processing the ID Token ', error);
          }
        });
    }

    // OIDC Version 15 upgrade: logout will get cancelled after page refresh if there is ongoing subscription for isAuthenticated$ and userData$.
    // so, end the subscription, to get the logout call
    this.onDestroyUserData$.next(true);
    this.onDestroyUserData$.complete();
  }

  /**
   * This is called when the authorization callback has been triggered
   * If authorized then it should re-route the user to the deep link location if present
   * otherwise the user will be redirected to the unauthorized route.
   * @param isAuthenticated Whether the user is authenticated
   */
  private onAuthorizationResult(isAuthenticated: boolean): void {
    // If this is an auth callback we will process the results
    if (Jwt.hasTokens(this.hashedEntryPoint) || isAuthenticated) {
      if (this.isNewAuthentication) {
        this.processAuthenticationResult(isAuthenticated);
      } else {
        console.debug(
          'Existing authentication found, forcing session refresh.'
        );
        this.refreshSession(isAuthenticated);
      }
    } else {
      // otherwise we will trigger auto-login process
      this.autoLogin(isAuthenticated);
    }
  }

  private processAuthenticationResult(isAuthenticated: boolean): void {
    this.releaseEvent.emit(true);
    if (isAuthenticated) {
      this.redirectService.navigateToRedirect();
      this.onDestroy$.next(true);
      this.onDestroy$.complete();
    } else {
      this.redirectService.navigateToForbidden();
    }
  }

  /**
   * Refreshes the entire session. The purpose of this logic is to require the user to log in
   * when the authentication exists at the app's session storage level, but no longer at IDP level.
   * For example: the user logs into this app in one tab, then the user logs out of the IDP from another app
   * in the same browser Window, (thus killing the IDP session). If the user then refreshes this app's tab,
   * a login should be required (only if autologin=true OR the route requires the user to be logged in)
   */
  private refreshSession(isAuthenticated: boolean) {
    console.debug('refreshing session');
    this.oidcSecurityService.forceRefreshSession().subscribe({
      next: value => {
        const isAuthenticatedWithAValidSession = value?.isAuthenticated
          ? true
          : false;
        console.debug(
          'Force refresh session resulted in: ',
          isAuthenticatedWithAValidSession
            ? 'authenticated'
            : 'not authenticated'
        );
        // isAuthenticatedWithAValidSession won't be true if the application is running on localhost AND using an incognito Window,
        // due to browsers (such as Chrome) filtering out cookies, which makes the silent renew to fail with error:
        // error=login_required&error_description=The+client+specified+not+to+prompt%2C+but+the+user+is+not+logged+in.
        if (isAuthenticatedWithAValidSession) {
          this.processAuthenticationResult(true);
        } else {
          this.autoLogin(false);
        }
      }
    });
  }

  /**
   * get query param value from hashed url
   */
  private getHashedQuery(currentPath: string, queryParam: string): string {
    const searchParams = new URLSearchParams(currentPath?.split('?')?.[1]);
    return searchParams?.get(queryParam);
  }

  /**
   * Executes the auto-login logic which will capture the current page and redirect as necessary
   */
  private autoLogin(isAuthenticated: boolean) {
    // OIDC Version 15 upgrade: handle any login error (this was not handled with oidc 9 implementation of csx-security)
    const redirect = new URL(this.configService.getConfig()?.redirectUrl);
    const errorParam = 'error';
    const descriptionParam = 'error_description';
    // OIDC Version 15 upgrade: if there is login error and handleError config is set to true then we navigate the user to unauthorized page.
    const error = this.redirectService.getQueryParam(errorParam);
    const description = this.redirectService.getQueryParam(descriptionParam);
    if (
      this.configService.handleError &&
      this.redirectService.currentPath.startsWith(redirect.pathname) &&
      error
    ) {
      console.error(
        'Failed to authenticate. Reason: [' +
          error +
          `] ${description ? ' Description: [' + description + ']' : ''}`
      );
      this.redirectService.navigateToForbidden();
      this.releaseEvent.emit(true);
      return;
    }

    if (isAuthenticated || Jwt.hasTokens(this.hashedEntryPoint)) {
      this.releaseEvent.emit(true);
      return;
    }

    if (this.configService.autoLogin) {
      console.debug('AutoLogin: Authorizing');
      this.captureLandingPage();

      // A bug was introduced in a post 9.0.3 release of the base library
      // which causes the nonce to be cleared in certain scenarios
      // This checks for and corrects that behavior
      // See: https://github.com/damienbod/angular-auth-oidc-client/issues/479
      if (this.isAuthorizationInProgress()) {
        let retries = 0;
        const timer = setInterval(() => {
          if (!this.isAuthorizationInProgress() || retries++ > 20) {
            this.oidcSecurityService.authorize();
            clearInterval(timer);
          }
        }, 50);
      } else {
        this.oidcSecurityService.authorize();
      }
      // We do not trigger the releaseEvent here
      // Doing so would allow angular to present the not-authorized screen
      // before authorization has occurred
    } else {
      this.releaseEvent.emit(true);
      console.info(
        'Autologin is disabled; to enable this update the client configuration'
      );
    }
  }

  private isAuthorizationInProgress(): boolean {
    const nonce = this.getValueFromStorage(OidcLoaderService.AUTHNONCE);
    return !(nonce === '' || nonce === undefined || nonce === null);
  }

  private getValueFromStorage(key: string) {
    const config = this.oidcSecurityService.getConfigurations()?.[0];
    const id = config?.configId;
    let val = this.securityStorageService.read(id);
    if (val) {
      val = JSON.parse(val);
      val = val?.[key];
    }
    return val;
  }

  /**
   * Identifies the user's current location
   */
  private captureLandingPage() {
    this.redirectService.saveRedirectPath(this.redirectPath);
  }

  get configService(): OidcConfigService {
    if (this._configService === null) {
      this._configService = this.injector.get(OidcConfigService);
    }

    if (this._configService === undefined || this._configService === null) {
      throw new Error(
        'Null was injected for OidcConfigService; this is most likely a side effect of other errors. If this is not the case then please notify the library creator.'
      );
    }

    return this._configService;
  }

  get eventService(): PublicEventsService {
    if (this._eventService === null) {
      this._eventService = this.injector.get(PublicEventsService);
    }

    if (this._eventService === undefined || this._eventService === null) {
      throw new Error(
        'Null was injected for PublicEventsService; this is most likely a side effect of other errors. If this is not the case then please notify the library creator.'
      );
    }

    return this._eventService;
  }
  get oidcSecurityService(): OidcSecurityService {
    if (this._oidSecurityService === null) {
      this._oidSecurityService = this.injector.get(OidcSecurityService);
    }

    if (
      this._oidSecurityService === undefined ||
      this._oidSecurityService === null
    ) {
      throw new Error(
        'Null was injected for OidcSecurityService; this is most likely a side effect of other errors. If this is not the case then please notify the library creator.'
      );
    }

    return this._oidSecurityService;
  }
  get oidcAuthenticationService(): OidcAuthenticationService {
    if (this._oidcAuthenticationService === null) {
      this._oidcAuthenticationService = this.injector.get(
        OidcAuthenticationService
      );
    }

    if (
      this._oidcAuthenticationService === undefined ||
      this._oidcAuthenticationService === null
    ) {
      throw new Error(
        'Null was injected for OidcSecurityService; this is most likely a side effect of other errors. If this is not the case then please notify the library creator.'
      );
    }

    return this._oidcAuthenticationService;
  }

  get locationStrategy(): LocationStrategy {
    if (this._locationStrategy === null) {
      this._locationStrategy = this.injector.get(LocationStrategy);
    }

    if (
      this._locationStrategy === undefined ||
      this._locationStrategy === null
    ) {
      throw new Error(
        'Null was injected for LocationStrategy; this is most likely a side effect of other errors. If this is not the case then please notify the library creator.'
      );
    }

    return this._locationStrategy;
  }

  get securityConfig(): SecurityConfig {
    if (this._secConfig === null) {
      this._secConfig = this.injector.get(SecurityConfigInjectionToken);
    }

    if (this._secConfig === undefined || this._secConfig === null) {
      throw new Error(
        'Null was injected for SecurityConfig; this is most likely a side effect of other errors. If this is not the case then please notify the library creator.'
      );
    }

    return this._secConfig;
  }

  get storageService(): StorageService {
    if (this._storageService === null) {
      this._storageService = this.injector.get(StorageService);
    }

    if (this._storageService === undefined || this._storageService === null) {
      throw new Error(
        'Null was injected for StorageService; this is most likely a side effect of other errors. If this is not the case then please notify the library creator.'
      );
    }

    return this._storageService;
  }

  get redirectService(): RedirectService {
    if (this._redirectService === null) {
      this._redirectService = this.injector.get(RedirectService);
    }

    if (this._redirectService === undefined || this._redirectService === null) {
      throw new Error(
        'Null was injected for RedirectService; this is most likely a side effect of other errors. If this is not the case then please notify the library creator.'
      );
    }

    return this._redirectService;
  }

  get securityStorageService(): AbstractSecurityStorage {
    if (this._oidcSecurityStorage === null) {
      this._oidcSecurityStorage = this.injector.get(AbstractSecurityStorage);
    }

    if (
      this._oidcSecurityStorage === undefined ||
      this._oidcSecurityStorage === null
    ) {
      throw new Error(
        'Null was injected for AbstractSecurityStorage; this is most likely a side effect of other errors. If this is not the case then please notify the library creator.'
      );
    }

    return this._oidcSecurityStorage;
  }
}
